import React, { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Lock } from 'lucide-react';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  type AppRole,
  type Permission,
  type UserProfile,
  type CurrentUserData,
  type CompanySettings,
  type NotificationSettings,
  type RoleStats,
  type PermissionsByCategory,
  type UpdateUserPermissionsParams,
} from '@/lib/settingsTypes';
import { UserManagementTab } from '@/components/settings/UserManagementTab';
import { InvitationsTab } from '@/components/settings/InvitationsTab';
import { CompanySettingsTab } from '@/components/settings/CompanySettingsTab';
import { NotificationsTab } from '@/components/settings/NotificationsTab';

const Settings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, hasPermission, canAccessPage, signOut, loading: permissionsLoading } = usePermissions();
  const isMobile = useIsMobile();

  const [editingUserPermissions, setEditingUserPermissions] = useState<string | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    company_name: 'Blue Whale Enterprises',
    address: '',
    phone: '',
    email: '',
    currency: 'USD',
    fiscal_year: 'january'
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    low_stock_alerts: true,
    new_orders: true,
    overdue_invoices: true,
    weekly_reports: false,
    low_stock_threshold: '20',
    overdue_days: '30',
    notification_email: ''
  });

  const { data: currentUserData, error: currentUserError } = useQuery<CurrentUserData>({
    queryKey: ['current-user-data'],
    queryFn: async (): Promise<CurrentUserData> => {
      if (!user?.id) throw new Error('No user ID available');
      const [roleData, superAdminData] = await Promise.all([
        supabase.rpc('get_current_user_role'),
        supabase.rpc('is_super_admin', { _user_id: user.id })
      ]);
      if (roleData.error) throw roleData.error;
      if (superAdminData.error) throw superAdminData.error;
      return {
        role: roleData.data as AppRole,
        isSuperAdmin: superAdminData.data as boolean
      };
    },
    enabled: !!user?.id,
  });

  const { data: permissions = [] } = useQuery<Permission[]>({
    queryKey: ['permissions'],
    queryFn: async (): Promise<Permission[]> => {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<UserProfile[]>({
    queryKey: ['users-with-roles'],
    queryFn: async (): Promise<UserProfile[]> => {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');
      if (profilesError) throw profilesError;
      
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');
      if (rolesError) throw rolesError;
      
      const usersWithRoles = profiles?.map(profile => ({
        ...profile,
        user_roles: userRoles?.filter(role => role.user_id === profile.user_id) || []
      })) || [];
      
      return usersWithRoles;
    },
  });

  const roleStats: RoleStats = users.reduce((acc, user) => {
    const role = user.user_roles?.[0]?.role || 'employee';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {} as RoleStats);

  const permissionsByCategory: PermissionsByCategory = permissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as PermissionsByCategory);

  const updateUserPermissionsMutation = useMutation({
    mutationFn: async ({ userId, permissions: userPermissions }: UpdateUserPermissionsParams) => {
      if (currentUserData?.role !== 'admin' && !currentUserData?.isSuperAdmin) {
        throw new Error('You do not have permission to modify user permissions');
      }

      const { error: deleteError } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', userId);
      if (deleteError) throw deleteError;

      if (userPermissions.length > 0) {
        const permissionRecords = userPermissions.map(permissionId => ({
          user_id: userId,
          permission_id: permissionId,
          granted_by: user?.id
        }));
        const { error } = await supabase
          .from('user_permissions')
          .insert(permissionRecords);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Permissions Updated", description: "User permissions have been successfully updated." });
      queryClient.invalidateQueries({ queryKey: ['users-with-roles'] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update permissions", variant: "destructive" });
    },
  });

  const saveCompanySettingsMutation = useMutation({
    mutationFn: async (settings: CompanySettings) => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return settings;
    },
    onSuccess: () => {
      toast({ title: "Settings saved", description: "Company settings have been updated successfully." });
    },
  });

  const saveNotificationSettingsMutation = useMutation({
    mutationFn: async (settings: NotificationSettings) => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return settings;
    },
    onSuccess: () => {
      toast({ title: "Settings saved", description: "Notification settings have been updated successfully." });
    },
  });

  if (permissionsLoading || !currentUserData) {
    if (currentUserError) {
      return (
        <AppLayout currentPage="settings">
          <div className="min-h-screen flex items-center justify-center">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-center text-destructive">Error Loading Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center text-muted-foreground">
                  Failed to load user data: {(currentUserError as any)?.message || 'Unknown error'}
                </p>
              </CardContent>
            </Card>
          </div>
        </AppLayout>
      );
    }
    return (
      <AppLayout currentPage="settings">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!canAccessPage([PERMISSIONS.VIEW_SETTINGS])) {
    return (
      <AppLayout currentPage="settings">
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-center text-destructive flex items-center justify-center gap-2">
                <Lock className="h-5 w-5" />
                Access Restricted
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground">
                You don't have permission to view settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (currentUserData && (currentUserData.role !== 'admin' && !currentUserData.isSuperAdmin)) {
    return (
      <AppLayout currentPage="settings">
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-center text-destructive">Access Restricted</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground">
                You don't have permission to access the settings page. Only administrators can view and modify system settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const handleEditUserPermissions = async (userId: string) => {
    try {
      const { data: userPermissions, error } = await supabase
        .from('user_permissions')
        .select('permission_id')
        .eq('user_id', userId);
      
      if (error) throw error;
      const currentPermissionIds = userPermissions?.map(up => up.permission_id) || [];
      setSelectedPermissions(currentPermissionIds);
      setEditingUserPermissions(userId);
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      toast({
        title: "Error",
        description: "Failed to load user permissions. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveUserPermissions = () => {
    if (editingUserPermissions) {
      updateUserPermissionsMutation.mutate({
        userId: editingUserPermissions,
        permissions: selectedPermissions
      });
      setEditingUserPermissions(null);
      setSelectedPermissions([]);
    }
  };

  return (
    <AppLayout currentPage="settings">
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="mobile-heading">Settings</h1>
          <p className="mobile-body text-muted-foreground">
            Manage your application settings and user permissions
          </p>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className={isMobile ? "grid w-full grid-cols-2 h-auto" : "grid w-full grid-cols-4 h-auto"}>
            <TabsTrigger 
              value="users" 
              className={isMobile ? "text-xs px-2 py-2.5" : "text-sm px-4 py-2.5"}
            >
              {isMobile ? "Users" : "User Management"}
            </TabsTrigger>
            <TabsTrigger 
              value="invitations" 
              className={isMobile ? "text-xs px-2 py-2.5" : "text-sm px-4 py-2.5"}
            >
              Invitations
            </TabsTrigger>
            {!isMobile && (
              <>
                <TabsTrigger value="company" className="text-sm px-4 py-2.5">
                  Company Settings
                </TabsTrigger>
                <TabsTrigger value="notifications" className="text-sm px-4 py-2.5">
                  Notifications
                </TabsTrigger>
              </>
            )}
          </TabsList>
          
          {isMobile && (
            <div className="grid grid-cols-2 gap-1 mt-2">
              <TabsList className="grid w-full grid-cols-1">
                <TabsTrigger value="company" className="text-xs px-2 py-2.5">
                  Company
                </TabsTrigger>
              </TabsList>
              <TabsList className="grid w-full grid-cols-1">
                <TabsTrigger value="notifications" className="text-xs px-2 py-2.5">
                  Notify
                </TabsTrigger>
              </TabsList>
            </div>
          )}

          <TabsContent value="users" className="space-y-6">
            <UserManagementTab
              users={users}
              usersLoading={usersLoading}
              roleStats={roleStats}
              currentUserData={currentUserData}
              currentUserId={user?.id}
              onEditUserPermissions={handleEditUserPermissions}
            />
          </TabsContent>

          <TabsContent value="invitations">
            <InvitationsTab
              refreshTrigger={refreshTrigger}
              onInvitationSent={() => setRefreshTrigger(prev => prev + 1)}
            />
          </TabsContent>

          <TabsContent value="company">
            <CompanySettingsTab
              companySettings={companySettings}
              onSettingsChange={setCompanySettings}
              onSave={() => saveCompanySettingsMutation.mutate(companySettings)}
              isSaving={saveCompanySettingsMutation.isPending}
            />
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationsTab
              notificationSettings={notificationSettings}
              onSettingsChange={setNotificationSettings}
              onSave={() => saveNotificationSettingsMutation.mutate(notificationSettings)}
              isSaving={saveNotificationSettingsMutation.isPending}
            />
          </TabsContent>
        </Tabs>

        {/* Edit Permissions Dialog */}
        <Dialog open={!!editingUserPermissions} onOpenChange={() => setEditingUserPermissions(null)}>
          <DialogContent className="mobile-dialog max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">Edit User Permissions</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 sm:space-y-4">
              {Object.entries(permissionsByCategory).map(([category, categoryPermissions]) => (
                <div key={category} className="space-y-2">
                  <h3 className="font-semibold text-xs sm:text-sm uppercase tracking-wide text-muted-foreground">
                    {category.replace('_', ' ')}
                  </h3>
                  <Separator />
                  <div className="space-y-2">
                    {categoryPermissions.map((permission) => (
                      <div key={permission.id} className="flex items-start space-x-3 py-2 touch-target">
                        <Checkbox
                          id={permission.id}
                          checked={selectedPermissions.includes(permission.id)}
                          onCheckedChange={(checked) => {
                            setSelectedPermissions(prev =>
                              checked
                                ? [...prev, permission.id]
                                : prev.filter(id => id !== permission.id)
                            );
                          }}
                          className="mt-1"
                        />
                        <div className="grid gap-1.5 leading-none flex-1">
                          <label
                            htmlFor={permission.id}
                            className="text-xs sm:text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {permission.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </label>
                          {permission.description && (
                            <p className="text-xs text-muted-foreground">{permission.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setEditingUserPermissions(null)}
                className="touch-target w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveUserPermissions} 
                disabled={updateUserPermissionsMutation.isPending}
                className="touch-target w-full sm:w-auto"
              >
                {updateUserPermissionsMutation.isPending ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default Settings;
